<script setup lang="ts">
import { gsap } from 'gsap'

const root = ref<HTMLElement | null>(null)
const { locale } = useI18n()
const calendarSrc = computed(() =>
  locale.value === 'en' ? '/bookforme_calendar_screen_en.svg' : '/bookforme_calendar_screen.svg',
)

onMounted(() => {
  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

  tl.fromTo('.fh-title',
    { opacity: 0, y: 40 },
    { opacity: 1, y: 0, duration: 0.8, ease: 'power4.out' },
  )
    .fromTo('.fh-subtitle',
      { opacity: 0, y: 24 },
      { opacity: 1, y: 0, duration: 0.65 },
      '-=0.4',
    )
    .fromTo('.fh-visual',
      { opacity: 0, y: 60 },
      { opacity: 1, y: 0, duration: 1.1, ease: 'power3.out' },
      '-=0.3',
    )

  // Pin each blob to a screen edge as its resting position. Runs on every screen
  // so the gradient wash is composed correctly even where we don't animate.
  const blobEls = Array.from(root.value?.querySelectorAll<SVGElement>('.fh-blob') ?? [])
  const radii = blobEls.map(el => el.clientWidth / 2)
  let w = root.value?.clientWidth ?? window.innerWidth
  let h = root.value?.clientHeight ?? window.innerHeight
  const edges = [
    { x: 0, y: h * 0.5 },
    { x: w, y: h * 0.5 },
    { x: w * 0.5, y: h },
  ]
  blobEls.forEach((el, i) => {
    const e = edges[i]
    gsap.set(el, { x: e.x - radii[i], y: e.y - radii[i] })
  })

  // Continuous ambient motion (visual float + blob breathing/wander) is the
  // expensive bit: transform-animating three heavily-blurred 600px SVGs every
  // frame thrashes the mobile compositor and makes the hero text appear to
  // vibrate. Gate it to large screens with motion allowed; phones keep the blobs
  // static as a still gradient wash. matchMedia auto-cleans on unmount/teardown.
  const mm = gsap.matchMedia()
  mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
    // Continuous float on the visual (delay keeps it from fighting the entrance)
    gsap.to('.fh-visual', {
      y: -16,
      duration: 3,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
      delay: 1.5,
    })

    // Blob breathing
    gsap.to('.fh-blob-1', { scale: 1.12, opacity: 0.4, duration: 9, repeat: -1, yoyo: true, ease: 'sine.inOut' })
    gsap.to('.fh-blob-2', { scale: 1.14, opacity: 0.52, duration: 11, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: -3 })
    gsap.to('.fh-blob-3', { scale: 1.1, opacity: 0.3, duration: 13, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: -6 })

    // Blob wander — each blob picks a fresh random target on arrival
    const wander = (i: number) => {
      const r = radii[i]
      gsap.to(blobEls[i], {
        x: gsap.utils.random(Math.min(r, w - r), Math.max(r, w - r)) - r,
        y: gsap.utils.random(Math.min(r, h - r), Math.max(r, h - r)) - r,
        duration: gsap.utils.random(10, 16),
        ease: 'sine.inOut',
        onComplete: () => wander(i),
      })
    }
    blobEls.forEach((_, i) => wander(i))

    const onResize = () => {
      w = root.value?.clientWidth ?? w
      h = root.value?.clientHeight ?? h
    }
    window.addEventListener('resize', onResize, { passive: true })

    return () => {
      gsap.killTweensOf(blobEls)
      gsap.killTweensOf('.fh-visual')
      window.removeEventListener('resize', onResize)
    }
  })

  onUnmounted(() => mm.revert())
})
</script>

<template>
  <section
    id="features-hero"
    ref="root"
    class="min-h-svh pt-20 md:pt-24 lg:pt-28 pb-12 md:pb-16 lg:pb-20 bg-white relative overflow-hidden flex items-center">

    <!-- Ambient gradient blobs -->
    <svg class="fh-blob fh-blob-1 absolute top-0 left-0 w-[38rem] h-[38rem] opacity-30 blur-3xl pointer-events-none" viewBox="0 0 200 200" aria-hidden="true">
      <defs>
        <linearGradient id="fhBlobGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#5CC8FF" stop-opacity="0.7" />
          <stop offset="55%" stop-color="#2EACFB" stop-opacity="0.62" />
          <stop offset="100%" stop-color="#2E83FB" stop-opacity="0.55" />
        </linearGradient>
      </defs>
      <path d="M44.7,-69.8C58.4,-61.8,69.9,-49.5,76.7,-35C83.6,-20.5,85.7,-3.7,82.2,11.8C78.7,27.3,69.6,41.4,58,52.7C46.4,64,32.3,72.6,16.6,76.7C0.9,80.8,-16.4,80.5,-31.8,74.3C-47.2,68,-60.7,55.8,-69.4,41C-78.1,26.2,-82,8.8,-79.6,-7.4C-77.3,-23.6,-68.8,-38.6,-57.1,-48.1C-45.4,-57.7,-30.5,-61.8,-15.5,-66.3C-0.6,-70.8,14.4,-75.7,28.5,-77C42.7,-78.3,56,-77.8,44.7,-69.8Z" transform="translate(100 100)" fill="url(#fhBlobGrad1)" />
    </svg>

    <svg class="fh-blob fh-blob-2 absolute top-0 left-0 w-[34rem] h-[34rem] opacity-40 blur-3xl pointer-events-none" viewBox="0 0 200 200" aria-hidden="true">
      <defs>
        <linearGradient id="fhBlobGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#F23FB0" stop-opacity="0.7" />
          <stop offset="55%" stop-color="#CB2097" stop-opacity="0.6" />
          <stop offset="100%" stop-color="#8B2FC9" stop-opacity="0.5" />
        </linearGradient>
      </defs>
      <path d="M38.5,-64.7C51.1,-57.7,63.2,-49.1,70.8,-37.3C78.4,-25.5,81.5,-10.5,79.7,3.6C77.9,17.7,71.2,30.9,62.1,42.5C53,54.1,41.5,64.1,28.3,69.8C15.1,75.5,0.2,76.9,-14.8,74.3C-29.8,71.7,-44.9,65.1,-56.4,54.4C-67.9,43.7,-75.8,28.9,-78.5,13.1C-81.2,-2.7,-78.7,-19.5,-71.2,-33.4C-63.7,-47.3,-51.2,-58.3,-37.6,-65.1C-24,-71.9,-9.3,-74.5,2.4,-78.3C14.1,-82.1,25.9,-71.7,38.5,-64.7Z" transform="translate(100 100)" fill="url(#fhBlobGrad2)" />
    </svg>

    <svg class="fh-blob fh-blob-3 absolute top-0 left-0 w-[30rem] h-[30rem] opacity-20 blur-3xl pointer-events-none" viewBox="0 0 200 200" aria-hidden="true">
      <defs>
        <linearGradient id="fhBlobGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#6366F1" stop-opacity="0.65" />
          <stop offset="100%" stop-color="#2E83FB" stop-opacity="0.5" />
        </linearGradient>
      </defs>
      <path d="M44.7,-69.8C58.4,-61.8,69.9,-49.5,76.7,-35C83.6,-20.5,85.7,-3.7,82.2,11.8C78.7,27.3,69.6,41.4,58,52.7C46.4,64,32.3,72.6,16.6,76.7C0.9,80.8,-16.4,80.5,-31.8,74.3C-47.2,68,-60.7,55.8,-69.4,41C-78.1,26.2,-82,8.8,-79.6,-7.4C-77.3,-23.6,-68.8,-38.6,-57.1,-48.1C-45.4,-57.7,-30.5,-61.8,-15.5,-66.3C-0.6,-70.8,14.4,-75.7,28.5,-77C42.7,-78.3,56,-77.8,44.7,-69.8Z" transform="translate(100 100)" fill="url(#fhBlobGrad3)" />
    </svg>

    <!-- Dot grid -->
    <div
      class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none"
      aria-hidden="true" />

    <div class="container relative z-10 flex flex-col items-center text-center">

      <!-- Headline -->
      <h1 class="fh-title text-[clamp(2.25rem,5vw,4rem)] font-bold leading-[1.15] rtl:leading-[1.4] mb-6 lg:mb-7 max-w-4xl">
        <span class="text-sky-blue">{{ $t('page.features.hero.title_1') }}</span>{{ $t('page.features.hero.title_2') }}<br>
        {{ $t('page.features.hero.title_3') }}<span class="text-heart-purple">{{ $t('page.features.hero.title_4') }}</span><br>
        <span class="text-heart-purple">{{ $t('page.features.hero.title_5') }}</span>
      </h1>

      <!-- Subtitle -->
      <p class="fh-subtitle text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed max-w-2xl mb-10 lg:mb-12">
        {{ $t('page.features.hero.subtitle') }}
      </p>

      <!-- Calendar image -->
      <div class="fh-visual w-full max-w-2xl md:max-w-3xl lg:max-w-5xl">
        <img
          :src="calendarSrc"
          alt="bookforme calendar dashboard"
          class="w-full h-auto drop-shadow">
      </div>
    </div>
  </section>
</template>
